import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Avatar,
  CssBaseline,
  Link,
  Paper,
  Box,
  Grid,
  Typography,
  makeStyles,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Collapse,
  IconButton,
  InputAdornment,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import {
  People,
  Edit,
  Delete,
  PersonAdd,
  Search,
  ExpandMore,
  ExpandLess,
} from "@material-ui/icons";
import { useFirebase } from "../../Firebase/FirebaseContext";
import CustomButton from "../Buttons/CustomButton";
// Les deux sections retirees du menu le 07/09, et que l'on peut rendre a
// quelques utilisateurs depuis cet ecran (retour du 08/09). La regle et ses
// limites vivent dans Utils/sectionsReservees.
import {
  SECTIONS_RESERVEES,
  CHAMP_FIRESTORE,
  normaliserSections,
  libelleSection,
} from "../../Utils/sectionsReservees";
// Le regroupement par SEL, demande le 22/09. La regle et ce qu'elle fait des
// fiches abimees vivent dans Utils/droitsParSel, avec leurs essais.
import {
  LABO_INCONNU,
  SANS_SEL,
  grouperParSel,
  indexerLaboratoires,
  nomLaboratoire,
} from "../../Utils/droitsParSel";
import {
  collection,
  doc,
  setDoc,
  getDocs,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";

/**
 * Le role, en francais.
 *
 * L'ecran d'avant ecrivait « Hotesse » pour `hostess` et « Preleveur » pour
 * TOUT le reste — y compris pour un `admin` ou une `borne`, qui se voyaient
 * donc etiquetes preleveurs. On nomme ce qu'on connait, et on rend la valeur
 * brute pour le reste plutot que de mentir.
 */
function libelleRole(statut) {
  const noms = {
    hostess: "Hôtesse",
    technician: "Préleveur",
    secretary: "Secrétaire",
    internal_phlebotomist: "Préleveur interne",
    external_phlebotomist: "Préleveur externe",
    borne: "Borne",
    admin: "Administrateur",
  };
  return noms[statut] || statut || "—";
}

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(4),
    maxWidth: 1400,
    margin: "0 auto",
  },
  header: {
    marginBottom: theme.spacing(4),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  headerIcon: {
    fontSize: 48,
    color: theme.palette.primary.main,
  },
  title: {
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  tableContainer: {
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    // `hidden` COUPAIT les dernieres colonnes, sans barre pour y aller.
    // Depuis le 22/09 le tableau porte une colonne par section reservee : il
    // fait 1 479 px pour 896 px de place, et « Actions » — donc Modifier et
    // Supprimer — tombait hors de l'ecran, atteignable par rien.
    overflowX: "auto",
  },
  tableHead: {
    backgroundColor: theme.palette.primary.main,
    "& .MuiTableCell-head": {
      // Le fond se pose sur la CELLULE, pas seulement sur la ligne : le theme
      // donne aux cellules d'entete un fond clair qui recouvrait le bleu, et
      // l'entete se lisait donc blanc sur blanc — invisible.
      backgroundColor: theme.palette.primary.main,
      color: "#FFFFFF",
      fontWeight: 600,
      fontSize: "0.95rem",
    },
  },
  tableRow: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  actionButton: {
    marginRight: theme.spacing(1),
    minWidth: 40,
    padding: theme.spacing(1),
  },
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 16,
      padding: theme.spacing(2),
      minWidth: 500,
    },
  },
  dialogTitle: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  // Le bandeau d'une SEL : c'est lui qu'on parcourt des yeux, donc il doit se
  // distinguer d'une ligne de personne sans avoir a lire.
  ligneSel: {
    backgroundColor: theme.palette.grey[100],
    cursor: "pointer",
    "&:hover": { backgroundColor: theme.palette.grey[200] },
  },
  titreSel: {
    fontWeight: 700,
    fontSize: "1.05rem",
  },
  compteurSel: {
    marginLeft: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontWeight: 400,
    fontSize: "0.85rem",
  },
  selAnomalie: {
    backgroundColor: "#fff7ed",
    "&:hover": { backgroundColor: "#ffedd5" },
  },
  recherche: {
    marginBottom: theme.spacing(2),
    maxWidth: 480,
  },
  celluleDroit: {
    textAlign: "center",
    padding: theme.spacing(0.5),
  },
  enteteDroit: {
    textAlign: "center",
    // Un libelle de section peut faire trois mots (« Réglages du laboratoire ») :
    // on le laisse tenir sur deux lignes plutot que d'elargir la colonne.
    maxWidth: 110,
    lineHeight: 1.25,
  },
}));

export default function UserManagement() {
  const { firebase } = useFirebase();
  const classes = useStyles();
  const [users, setUsers] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  const [laboratories, setLaboratories] = useState([]);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedUserToDelete, setSelectedUserToDelete] = useState(null);
  // Ce qui a empeche l'enregistrement, affiche dans la fenetre plutot que
  // laisse dans la console (18/09).
  const [erreurEnregistrement, setErreurEnregistrement] = useState(null);
  // Le tableau par SEL (22/09) : recherche, SEL depliees, et la case en cours
  // d'ecriture pour qu'un clic ne puisse pas partir deux fois.
  const [recherche, setRecherche] = useState("");
  const [selOuvertes, setSelOuvertes] = useState({});
  const [enEcriture, setEnEcriture] = useState(null);
  const [erreurDroit, setErreurDroit] = useState(null);

  const fetchUsers = async () => {
    const usersRef = collection(firebase.db, "users");
    const querySnapshot = await getDocs(usersRef);
    const usersData = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setUsers(usersData);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleOpenEditDialog = (user) => {
    setEditUser(user);
    setEditedFields({
      name: user.name,
      email: user.id,
      status: user.status,
      lab: user?.belongs_to?.id,
      sections: normaliserSections(user?.[CHAMP_FIRESTORE]),
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setErreurEnregistrement(null);
  };

  const handleFieldChange = (field, value) => {
    setEditedFields((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  /** Coche ou decoche une section reservee pour l'utilisateur en cours. */
  const basculerSection = (cle) => {
    setEditedFields((prev) => {
      const actuelles = prev.sections || [];
      return {
        ...prev,
        sections: actuelles.includes(cle)
          ? actuelles.filter((c) => c !== cle)
          : [...actuelles, cle],
      };
    });
  };

  /**
   * Coche ou decoche un droit DIRECTEMENT dans le tableau, sans passer par la
   * fenetre de modification.
   *
   * C'est la difference entre l'ecran d'avant et celui-la, et c'est toute la
   * demande : donner un droit se faisait en ouvrant une fiche, en cochant, en
   * enregistrant, en refermant — quatre gestes par personne, sur 548 fiches.
   * Ici, un clic ecrit le champ et rien d'autre.
   *
   * L'etat local est mis a jour AVANT l'ecriture pour que la case reponde tout
   * de suite, et remis dans son etat d'avant si Firestore refuse. Un refus se
   * dit : c'est exactement le defaut qu'on a corrige le 18/09 dans la fenetre
   * de modification, et il n'a aucune raison de revenir ici.
   */
  const basculerDroit = async (fiche, cle) => {
    const avant = normaliserSections(fiche?.[CHAMP_FIRESTORE]);
    const apres = avant.includes(cle)
      ? avant.filter((c) => c !== cle)
      : normaliserSections([...avant, cle]);

    setErreurDroit(null);
    setEnEcriture(`${fiche.id}:${cle}`);
    const appliquer = (valeur) =>
      setUsers((liste) =>
        liste.map((u) => (u.id === fiche.id ? { ...u, [CHAMP_FIRESTORE]: valeur } : u))
      );
    appliquer(apres);

    try {
      await updateDoc(doc(firebase.db, "users", fiche.id), { [CHAMP_FIRESTORE]: apres });
    } catch (error) {
      appliquer(avant);
      setErreurDroit(
        error?.code === "permission-denied"
          ? "Modification refusée : votre compte n'a pas le droit d'écrire les fiches utilisateur."
          : error?.message || "L'enregistrement a échoué."
      );
    } finally {
      setEnEcriture(null);
    }
  };

  /**
   * Enregistre la fiche, et DIT si ca n'a pas marche.
   *
   * Nicolas, 18/09 : « JE N'y ai pas acces, quand j'essaye de me passer
   * administrateur, cela me dit que je n'ai pas les droits. »
   *
   * Deux choses etaient fausses ici :
   *
   * 1. UN ECHEC NE SE VOYAIT PAS. La fenetre se fermait quoi qu'il arrive,
   *    l'erreur partait dans la console, et l'ecran laissait croire que
   *    c'etait enregistre. Une ecriture refusee par les regles Firestore
   *    ressemblait donc exactement a une ecriture reussie — et la case cochee
   *    reapparaissait decochee sans qu'on sache pourquoi.
   *
   * 2. `belongs_to` PARTAIT EN CHAINE. Le champ est une REFERENCE de document
   *    partout ailleurs : la connexion fait `getDoc(fiche.belongs_to)`, et
   *    lit `belongs_to._key.path.segments[6]` pour trouver le laboratoire.
   *    Ecrire l'identifiant en texte cassait la fiche de la personne a sa
   *    prochaine connexion. On n'y touche donc que si le laboratoire a
   *    reellement change, et on ecrit une vraie reference.
   */
  const handleSaveChanges = async () => {
    setErreurEnregistrement(null);
    try {
      const userRef = doc(firebase.db, "users", editUser.id);
      const champs = {
        name: editedFields.name,
        email: editedFields.email,
        status: editedFields.status,
        // On renormalise avant d'ecrire : la fiche ne doit jamais porter une
        // cle de section qui n'existe plus.
        [CHAMP_FIRESTORE]: normaliserSections(editedFields.sections),
      };
      const labChoisi = editedFields.lab;
      if (labChoisi && labChoisi !== editUser?.belongs_to?.id) {
        champs.belongs_to = doc(firebase.db, "laboratories", labChoisi);
      }
      await updateDoc(userRef, champs);

      handleCloseEditDialog();
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      setErreurEnregistrement(
        error?.code === "permission-denied"
          ? "Cette modification a été refusée : votre compte n'a pas le droit d'écrire les fiches utilisateur. Demandez-la à un administrateur."
          : error?.message || "L'enregistrement a échoué."
      );
    }
  };

  const handleOpenDeleteConfirmation = (user) => {
    setSelectedUserToDelete(user);
    setDeleteConfirmationOpen(true);
  };

  const handleCloseDeleteConfirmation = () => {
    setSelectedUserToDelete(null);
    setDeleteConfirmationOpen(false);
  };

  const handleConfirmDeleteUser = async () => {
    // Delete the user from the database
    try {
      const userRef = doc(firebase.db, "users", selectedUserToDelete.id);
      await deleteDoc(userRef);

      // Fetch the updated users list
      fetchUsers();
      handleCloseDeleteConfirmation();
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  /** Deplie ou replie une SEL. */
  const basculerSel = (sel) =>
    setSelOuvertes((prev) => ({ ...prev, [sel]: !prev[sel] }));

  const laboParId = indexerLaboratoires(laboratories);
  const groupes = grouperParSel(users, laboratories, recherche);

  /*
    UNE RECHERCHE OUVRE CE QU'ELLE TROUVE.

    Sans cela, taper un nom ne montrerait qu'un bandeau de SEL replie : on
    croirait que la recherche n'a rien donne. Les SEL sont repliees par defaut
    — seize bandeaux tiennent a l'ecran, 548 lignes non.
  */
  useEffect(() => {
    if (!recherche.trim()) return;
    setSelOuvertes(Object.fromEntries(groupes.map((g) => [g.sel, true])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recherche]);

  const laboratoriesRef = collection(firebase.db, "laboratories");

  useEffect(() => {
    getDocs(laboratoriesRef).then((querySnapshot) => {
      const labs = querySnapshot.docs.map((doc) => ({
        ...doc.data(),
        path: doc.ref.path,
        value: doc.id,
      }));
      setLaboratories(labs);
    });
  }, []);

  return (
    <>
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        aria-labelledby="edit-dialog-title"
        className={classes.dialog}
      >
        <DialogTitle id="edit-dialog-title" className={classes.dialogTitle}>
          Modifier l'utilisateur
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <TextField
              label="Nom"
              value={editedFields.name}
              onChange={(e) => handleFieldChange("name", e.target.value)}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Email"
              value={editedFields.email}
              onChange={(e) => handleFieldChange("email", e.target.value)}
              fullWidth
              margin="normal"
            />
            <InputLabel id="status-label">Statut</InputLabel>
            <Select
              labelId="status-label"
              value={editedFields.status}
              onChange={(e) => handleFieldChange("status", e.target.value)}
            >
              <MenuItem value="hostess">Hotesse</MenuItem>
              <MenuItem value="technician">Préleveur</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel id="lab-label">Laboratoire</InputLabel>
            <Select
              labelId="lab-label"
              value={editedFields.lab}
              onChange={(e) => handleFieldChange("lab", e.target.value)}
            >
              {laboratories.map((lab) => (
                <MenuItem key={lab.path} value={lab.value}>
                  {lab.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/*
            LES SECTIONS RESERVEES.

            Retour du 08/09 : « retire File d'envoi et reglages laboratoire.
            Donne moi en Admin la possibilite d'associer ces fonctions
            uniquement a quelques utilisateurs. »

            Rien de coche = le menu d'aujourd'hui, a l'identique. C'est l'etat
            de toutes les fiches existantes, et c'est voulu : cet ecran
            n'ouvre rien tant que personne ne coche.
          */}
          <FormControl component="fieldset" fullWidth margin="normal">
            <FormLabel component="legend">Sections réservées</FormLabel>
            <Typography variant="caption" color="textSecondary">
              Ces sections sont retirées du menu par défaut. Cochez-les pour
              cette personne uniquement.
            </Typography>
            <FormGroup>
              {SECTIONS_RESERVEES.map((section) => (
                <FormControlLabel
                  key={section.cle}
                  control={
                    <Checkbox
                      color="primary"
                      checked={(editedFields.sections || []).includes(section.cle)}
                      onChange={() => basculerSection(section.cle)}
                    />
                  }
                  label={section.libelle}
                />
              ))}
            </FormGroup>
          </FormControl>
        </DialogContent>
        {erreurEnregistrement && (
          <Typography
            style={{
              margin: "0 24px",
              padding: "8px 10px",
              borderRadius: 8,
              background: "#fff1f2",
              border: "1px solid #fecdd3",
              color: "#9f1239",
              fontSize: 13,
            }}
          >
            {erreurEnregistrement}
          </Typography>
        )}
        <DialogActions>
          <Button onClick={handleCloseEditDialog} color="primary">
            Annuler
          </Button>
          <Button onClick={handleSaveChanges} color="primary">
            Sauvegarder
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmationOpen}
        onClose={handleCloseDeleteConfirmation}
        aria-labelledby="delete-confirmation-dialog-title"
        className={classes.dialog}
      >
        <DialogTitle
          id="delete-confirmation-dialog-title"
          className={classes.dialogTitle}
        >
          Confirmation de suppression
        </DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer cet utilisateur ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirmation} color="primary">
            Annuler
          </Button>
          <Button onClick={handleConfirmDeleteUser} color="primary">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <div className={classes.container}>
        <Box className={classes.header}>
          <People className={classes.headerIcon} />
          <Typography variant="h3" className={classes.title}>
            Gestion des utilisateurs
          </Typography>
        </Box>

        {/*
          LE TABLEAU PAR SEL.

          Nicolas, 22/09 a 07h25 : « J'imagine un grand tableau avec l'ensemble
          des SEL (et non l'ensemble des laboratoires) et en dessous l'ensemble
          des utilisateurs et leurs droits associes. »

          Ce qui existait avant : 548 lignes a plat, sans ordre, avec pour
          colonne de laboratoire l'identifiant Firestore brut. On ne pouvait ni
          trouver quelqu'un, ni voir qui avait quoi, ni donner un droit sans
          ouvrir puis refermer une fenetre.
        */}
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          Les colonnes de droite sont les sections retirées du menu par défaut.
          Cochez-les pour donner l&apos;accès, la case s&apos;enregistre toute
          seule.
        </Typography>

        <TextField
          className={classes.recherche}
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Chercher un nom, une adresse, un laboratoire, une SEL"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {erreurDroit && (
          <Typography
            style={{
              margin: "0 0 16px",
              padding: "8px 10px",
              borderRadius: 8,
              background: "#fff1f2",
              border: "1px solid #fecdd3",
              color: "#9f1239",
              fontSize: 13,
            }}
          >
            {erreurDroit}
          </Typography>
        )}

        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Adresse</TableCell>
                <TableCell>Rôle</TableCell>
                <TableCell>Laboratoire</TableCell>
                {SECTIONS_RESERVEES.map((section) => (
                  <TableCell key={section.cle} className={classes.enteteDroit}>
                    {section.libelle}
                  </TableCell>
                ))}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5 + SECTIONS_RESERVEES.length}>
                    <Typography color="textSecondary">
                      Aucun utilisateur ne correspond à « {recherche} ».
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {groupes.map((groupe) => {
                const anomalie =
                  groupe.sel === SANS_SEL || groupe.sel === LABO_INCONNU;
                const ouverte = !!selOuvertes[groupe.sel];
                return (
                  <React.Fragment key={groupe.sel}>
                    <TableRow
                      className={`${classes.ligneSel} ${anomalie ? classes.selAnomalie : ""}`}
                      onClick={() => basculerSel(groupe.sel)}
                    >
                      <TableCell colSpan={5 + SECTIONS_RESERVEES.length}>
                        <IconButton size="small" style={{ marginRight: 8 }}>
                          {ouverte ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                        <span className={classes.titreSel}>{groupe.sel}</span>
                        <span className={classes.compteurSel}>
                          {groupe.membres.length} utilisateur
                          {groupe.membres.length > 1 ? "s" : ""}
                          {groupe.avecDroits > 0 &&
                            ` · ${groupe.avecDroits} avec un accès réservé`}
                        </span>
                        {/* Les deux groupes d'anomalie disent ce qu'il y a a
                            reparer, plutot que de se faire passer pour une
                            SEL de plus. */}
                        {groupe.sel === LABO_INCONNU && (
                          <Chip
                            size="small"
                            label="fiches rattachées à un laboratoire supprimé"
                            style={{ marginLeft: 12 }}
                          />
                        )}
                        {groupe.sel === SANS_SEL && (
                          <Chip
                            size="small"
                            label="aucune SEL sur leur laboratoire"
                            style={{ marginLeft: 12 }}
                          />
                        )}
                      </TableCell>
                    </TableRow>

                    {ouverte &&
                      groupe.membres.map((user) => {
                        const droits = normaliserSections(user?.[CHAMP_FIRESTORE]);
                        return (
                          <TableRow key={user.id} className={classes.tableRow}>
                            <TableCell>{user.name || "—"}</TableCell>
                            <TableCell>{user.id}</TableCell>
                            <TableCell>{libelleRole(user.status)}</TableCell>
                            <TableCell>{nomLaboratoire(user, laboParId)}</TableCell>
                            {SECTIONS_RESERVEES.map((section) => {
                              const cle = `${user.id}:${section.cle}`;
                              return (
                                <TableCell
                                  key={section.cle}
                                  className={classes.celluleDroit}
                                >
                                  {enEcriture === cle ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    <Checkbox
                                      color="primary"
                                      size="small"
                                      checked={droits.includes(section.cle)}
                                      onChange={() => basculerDroit(user, section.cle)}
                                      inputProps={{
                                        "aria-label": `${section.libelle} pour ${user.id}`,
                                      }}
                                    />
                                  )}
                                </TableCell>
                              );
                            })}
                            <TableCell align="right">
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                startIcon={<Edit />}
                                onClick={() => handleOpenEditDialog(user)}
                                className={classes.actionButton}
                              >
                                Modifier
                              </Button>
                              <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                startIcon={<Delete />}
                                onClick={() => handleOpenDeleteConfirmation(user)}
                                className={classes.actionButton}
                              >
                                Supprimer
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );
}
